import React, { useState, useEffect } from "react";
import { ContactNameCell, ContactValueCell } from "./contactCells";
import ListParty from "./listParty";
import StatusPill from "./statusPill";
import TodayIcon from "@mui/icons-material/TodayOutlined";
import EventIcon from "@mui/icons-material/EventOutlined";
import PropTypes from "prop-types";
import { darken } from "@mui/material/styles";
import { makeStyles } from 'tss-react/mui';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
  Paper,
  Checkbox,
  Menu,
  MenuItem,
  LinearProgress,
  ListSubheader,
  Divider,
  ButtonBase,
} from "@mui/material";
import EnhancedTableToolbar from "./tableToolbar";
import EnhancedTableHead from "./tableHead";
import DeleteForeverConfirmation from "./deleteForever";
import ActionsSpeedDial from "./tableActions";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import AudioPlayer from "./audioPlayer";
import { isArray, isEmpty } from 'lodash';

const useStyles = makeStyles()((theme) => ({
  root: {
    overflow: "auto",
  },
  // A column that fills the window under the app bar: the title bar and the
  // pagination keep their height, and the rows scroll in what is left. The
  // pagination therefore stays in view however tall the title bar gets.
  paper: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "100vw",
    height: "calc(100vh - 56px)",
    [theme.breakpoints.up("sm")]: {
      height: "calc(100vh - 64px)",
    },
  },
  tableContainer: {
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "auto",
  },
  table: {
    width: "100%",
    overflow: "auto",
    "& .MuiTableCell-paddingCheckbox": {
      paddingLeft: "1rem",
    },
    "& .MuiTableCell-root": {
      padding: theme.spacing(0, 2),
    },
    "& .MuiTableRow-root.MuiTableRow-hover:hover": {
      background: theme.palette.background.default,
    },
  },
  tableBody: {
    height: `100%`,
  },
  actions: {
    padding: 0,
    paddingRight: "1rem",
  },
  progress: {
    flexShrink: 0,
    height: 2,
  },
  progressBar: {
    height: 2,
    backgroundColor: "rgba(77, 171, 245, 0.2)",
    "& .MuiLinearProgress-bar": {
      backgroundColor: theme.palette.primary.light,
    },
  },
  loadingText: {
    color: theme.palette.text.secondary,
  },
  footerBar: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    borderTop: `1px solid ${theme.palette.primary.hr}`,
  },
  footerAction: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  pagination: {
    marginLeft: "auto",
    maxWidth: "100%",
    [theme.breakpoints.up("md")]: {
      "& .MuiTablePagination-actions": {
        marginRight: theme.spacing(8),
      },
    },
  },
  urgent: {
    "&.MuiChip-root": {
      background: theme.palette.secondary.main,
      "&:hover": {
        background: darken(theme.palette.secondary.main, 0.25),
      },
    }
  },
  failed: {
    "&.MuiChip-root": {
      background: theme.palette.secondary.main,
      "&:hover": {
        background: darken(theme.palette.secondary.main, 0.25),
      },

    }
  },
  viewed: {
    "&.MuiChip-root": {
      background: theme.palette.primary.main,
      "&:hover": {
        background: darken(theme.palette.primary.main, 0.25),
      },
    }
  },
  new: {
    "&.MuiChip-root": {
      background: theme.palette.primary.success,
      "&:hover": {
        background: darken(theme.palette.primary.success, 0.25),
      },
    }
  },
  answered: {
    "&.MuiChip-root": {
      background: theme.palette.primary.success,
      "&:hover": {
        background: darken(theme.palette.primary.success, 0.25),
      },
    }
  },
  busy: {
    "&.MuiChip-root": {
      background: theme.palette.secondary.light,
      "&:hover": {
        background: darken(theme.palette.secondary.light, 0.25),
      },
    }
  },
  deleted: {
    "&.MuiChip-root": {
      background: theme.palette.text.softer,
      "&:hover": {
        background: darken(theme.palette.text.softer, 0.25),
      },
    }
  },
  noanswer: {
    "&.MuiChip-root": {
      background: theme.palette.text.secondary,
      "&:hover": {
        background: darken(theme.palette.text.secondary, 0.25),
      },
    }
  },
  chip: {
    color: "white",
  },
  // Call journal status: a soft tinted label with a dot, quieter than a
  // solid chip in a list that is mostly "Answered".
  journalStatus: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "3px 10px",
    borderRadius: 12,
    fontSize: 12.5,
    fontWeight: 500,
    lineHeight: 1.5,
    whiteSpace: "nowrap",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
  },
  // Status of a voicemail, fax or recording: same soft look, and a button.
  // Admin lists: the hours of a day, and a dash for a day that is off.
  adminHours: {
    fontSize: 14.5,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  adminMuted: {
    color: theme.palette.text.disabled,
  },
  statusButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "3px 4px 3px 10px",
    borderRadius: 12,
    fontFamily: "inherit",
    fontSize: 12.5,
    fontWeight: 500,
    lineHeight: 1.5,
    whiteSpace: "nowrap",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    transition: "filter 0.15s ease",
    "&:hover": {
      filter: "brightness(0.95)",
    },
  },
  statusButtonArrow: {
    fontSize: 18,
    marginLeft: -4,
    opacity: 0.8,
  },
  statusButton_new: {
    color: "#1f8f4a",
    backgroundColor: "rgba(46, 158, 85, 0.12)",
  },
  statusButton_viewed: {
    color: theme.palette.primary.main,
    backgroundColor: "rgba(1, 186, 239, 0.12)",
  },
  statusButton_urgent: {
    color: "#c2185b",
    backgroundColor: "rgba(233, 30, 99, 0.10)",
  },
  journalStatusDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    backgroundColor: "currentColor",
  },
  journalStatus_answered: {
    color: "#1f8f4a",
    backgroundColor: "rgba(46, 158, 85, 0.12)",
  },
  journalStatus_busy: {
    color: "#b26d00",
    backgroundColor: "rgba(232, 147, 12, 0.14)",
  },
  journalStatus_failed: {
    color: "#d0312d",
    backgroundColor: "rgba(229, 50, 45, 0.10)",
  },
  // Status menu: compact list with a colour dot per status and a tick on the
  // current one.
  statusMenuPaper: {
    minWidth: 184,
    marginTop: theme.spacing(0.5),
    borderRadius: 10,
    boxShadow: "0px 6px 24px rgba(0, 0, 0, 0.16)",
  },
  statusMenuTitle: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.07em",
    lineHeight: "28px",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    background: "transparent",
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    minHeight: 36,
    margin: theme.spacing(0, 0.75),
    padding: theme.spacing(0.5, 1.25),
    borderRadius: 6,
    fontSize: 14,
    lineHeight: 1.4,
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: theme.palette.action.selected,
      fontWeight: 500,
    },
  },
  menuItemDanger: {
    color: theme.palette.secondary.main,
  },
  statusLabel: {
    flex: 1,
  },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: theme.palette.text.disabled,
  },
  statusDot_new: {
    backgroundColor: theme.palette.primary.success,
  },
  statusDot_viewed: {
    backgroundColor: theme.palette.primary.main,
  },
  statusDot_urgent: {
    backgroundColor: theme.palette.secondary.main,
  },
  statusDot_deleted: {
    backgroundColor: theme.palette.text.softer,
  },
  statusCheck: {
    fontSize: 18,
    color: theme.palette.text.secondary,
  },
}));

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export default function EnhancedTable(props) {
  const { classes, cx } = useStyles();
  const {
    setError,
    type,
    selectable = false,
    rowData = [],
    headerData = [],
    title = "Table",
    menu = false,
    searchFunc = null,
    tab = "",
    history,
    clearFunction,
    hasClear = false,
    page = 0,
    setPage,
    rowsPerPage = 10,
    setRowsPerPage,
    noPagination = false,
    emptyText,
    footerAction,
    loading = false,
    handleDialClick,
    setCalling,
    setValue,
    setOpenContactInfo,
    setEditContactInfo,
    setContact,
    setEditTimeFrameDay,
    setTimeFrameDay,
    setEditHoliday,
    setHoliday,
    setForwardItem,
    setForward,
    statusChangeFunction,
    handleDelete,
    handleDeleteForever,
    useGroupFilter,
    createItemCallback,
    createItemTooltip,
    createGroupOptions,
    setFilterGroup,
    filterGroup,
    dense = false,
    setTitleChangeItem,
    setEditTitle,
    setAttachment,
    setRecipient,
    setSendEmail,
    statusOptions,
    refreshDataOnSameTab,
    handleRowClick,
    cellAlign,
  } = props;
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState(headerData.length > 0 ? headerData[0].label : "");
  const [selected, setSelected] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [deleteForever, setDeleteForever] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [play, setPlay] = useState(false);
  const [playTarget, setPlayTarget] = useState("");
  const [playTargetObject, setPlayTargetObject] = useState(null);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = rowData.map((n) => n);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, row) => {
    const selectedIndex = selected.indexOf(row);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, row);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  // The page that is shown. When the list gets shorter (a search, another
  // tab, a deleted row) the remembered page may no longer exist; the last
  // page that does is shown instead, so the list is never blank.
  const lastPage = Math.max(0, Math.ceil(rowData.length / rowsPerPage) - 1);
  const shownPage = Math.min(Math.max(Number(page) || 0, 0), lastPage);

  useEffect(() => {
    if (setPage && Number(page) > lastPage) {
      setPage(lastPage);
    }
    // eslint-disable-next-line
  }, [page, lastPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (row) => selected.indexOf(row) !== -1;

  // const emptyRows = 
  //   rowData.length > 0
  //     ? rowsPerPage - Math.min(rowsPerPage, rowData.length - page * rowsPerPage)
  //     : rowsPerPage - Math.min(rowsPerPage, rowData.length - page * rowsPerPage)-1;

  const handleStatusMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = (type) => {
    statusChangeFunction(type, currentRow.object);
    handleStatusMenuClose();
  };

  const handleStatusButtonClick = (e, item) => {
    setAnchorEl(e.currentTarget);
    setCurrentRow(item);
  }


  // item,row[item],row,title
  const renderTableCell = (row, rowValue, obj, title) => {
    if (
      title.toLowerCase() !== "fax" &&
      title.toLowerCase() !== "fax groups" &&
      title.toLowerCase() !== "call journal" &&
      title.toLowerCase() !== "voicemail" &&
      title.toLowerCase() !== "voicemail groups" &&
      title.toLowerCase() !== "call recordings" &&
      title.toLowerCase() !== "sms messages"
    ) {
      if (title.toLowerCase() === "time frame") {
        if (row === "active" || row === "allDay") {
          return renderTimeFrameTrueFalse(row, rowValue, obj)
        }
        else {
          return renderTimeFrameTime(row, rowValue, obj);
        }
      }
      else if (title.toLowerCase() === "holidays") {
        if (row === "active" || row === "allDay") {
          return renderTimeFrameTrueFalse(row, rowValue, obj)
        }
        else {
          return renderFromCell(row, rowValue, obj, title);
        }
      }
      else {
        if (type === "contact") {
          // Display only: the stored values are still used for calling and sorting.
          if (row === "phone" || row === "email") {
            return <ContactValueCell value={rowValue} kind={row} />;
          }
        }
        if (title.toLowerCase() !== "notifications") {
          return rowValue;
        }
        else {
          return getNotificationStatus(row, rowValue, obj, title);
        }
      }
    }
    else {
      if (row === "status") {
        return getStatus(row, rowValue, obj, title);
      }
      else if (row === "from" && title.toLowerCase() !== "fax groups") {
        return renderFromCell(row, rowValue, obj, title);
      }
      else {
        // A list may hand over a ready-made cell (the recordings title).
        if (React.isValidElement(rowValue)) {
          return rowValue;
        }
        let text = rowValue.toLowerCase() === "old"
          ? "Viewed"
          : rowValue.charAt(0).toUpperCase() + rowValue.slice(1);
        return <span>{text}</span>;
      }
    }
  }

  // "09:00:00" -> "9:00 AM"
  const clockTime = (value) => {
    const [h, m] = String(value || "").split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return value || "";
    return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
  };

  // The hours of a day of the time frame, in one cell.
  const renderTimeFrameTime = (row, item, obj) => {
    if (row !== "start") return item;
    if (!obj.active) return <span className={classes.adminMuted}>—</span>;
    return (
      <span className={classes.adminHours}>
        {obj.allDay ? "All day" : `${clockTime(obj.start)} – ${clockTime(obj.end)}`}
      </span>
    );
  };

  const renderTimeFrameTrueFalse = (row, item, obj) =>
    <StatusPill on={!!item} />

  // The call journal and the voicemail list build their own cell, with the
  // number on a second line; the others get the number as a tooltip.
  const renderFromCell = (type, item, row, title) =>
    (title.toLowerCase() === "call journal" || React.isValidElement(item)) ? item : (
      <Tooltip title={row.object.number} placement="right">
        <span>{item}</span>
      </Tooltip>
    )

  const getStatus = (type, item, row, title) => {
    let text = item.toLowerCase() === "old"
      ? "Viewed"
      : item.charAt(0).toUpperCase() + item.slice(1);

    if ((title.toLowerCase() === "fax" ||
      title.toLowerCase() === "fax groups" ||
      title.toLowerCase() === "voicemail" ||
      title.toLowerCase() === "voicemail groups" ||
      title.toLowerCase() === "call recordings" ||
      title.toLowerCase() === "sms messages")) {
      // A soft tinted button that opens the status menu. The whole label is
      // clickable, not only the arrow.
      const tone = item.toLowerCase() === "old" ? "viewed" : item.toLowerCase();
      return (
        <ButtonBase
          aria-controls="status-menu"
          aria-haspopup="true"
          aria-label={`status: ${text}. Change status`}
          onClick={(e) => handleStatusButtonClick(e, row)}
          className={cx(classes.statusButton, classes[`statusButton_${tone}`])}
        >
          <span className={classes.journalStatusDot} />
          {text}
          <ArrowDropDownIcon className={classes.statusButtonArrow} />
        </ButtonBase>
      );
    } else if (title.toLowerCase() === "call journal") {
      // The server sends ANSWERED / NO ANSWER / BUSY / FAILED.
      let classText = item.toLowerCase().replace(/\s/g, "");
      const label = item.charAt(0).toUpperCase() + item.slice(1).toLowerCase();
      return (
        <span className={cx(classes.journalStatus, classes[`journalStatus_${classText}`])}>
          <span className={classes.journalStatusDot} />
          {label}
        </span>
      );
    }
    return <span>{text}</span>;
  }

  const getNotificationStatus = (type, item) => {
    // The notifications list hands over ready-made cells (date, counts).
    if (React.isValidElement(item)) {
      return item;
    }
    const value = String(item === null || item === undefined ? "" : item);
    let text =
      value.toLowerCase() === "old"
        ? "Viewed"
        : value.charAt(0).toUpperCase() + value.slice(1);

    if (type.toLowerCase() === "status" && value !== "") {
      // The same soft labels as the lists these items come from.
      if (tab.toLowerCase() === "call") {
        // The server sends ANSWERED / NO ANSWER / BUSY / FAILED.
        const classText = value.toLowerCase().replace(/\s/g, "");
        return (
          <span className={cx(classes.journalStatus, classes[`journalStatus_${classText}`])}>
            <span className={classes.journalStatusDot} />
            {value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()}
          </span>
        );
      }
      const tone = value.toLowerCase() === "old" ? "viewed" : value.toLowerCase();
      return (
        <span className={cx(classes.journalStatus, classes[`statusButton_${tone}`])}>
          <span className={classes.journalStatusDot} />
          {text}
        </span>
      );
    }

    return <span>{text}</span>;
  };

  const statusDropdownMenu = () => {
    let statuses = ["New", "Viewed", "Urgent", "Deleted"];

    if (isArray(statusOptions) && !isEmpty(statusOptions)) {
      statuses = statusOptions;
    }

    return (
      <Menu
        id="status-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleStatusMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: { className: classes.statusMenuPaper },
          list: { dense: true, "aria-label": "change status" },
        }}
      >
        <ListSubheader className={classes.statusMenuTitle}>Change status</ListSubheader>
        {statuses.flatMap((type, index) => {
          const key = String(type).toLowerCase();
          // The data calls a viewed item "old".
          const current = String((currentRow && currentRow.status) || "").toLowerCase();
          const isCurrent = key === (current === "old" ? "viewed" : current);
          const danger = key === "deleted";
          return [
            danger && index > 0 ? <Divider key={`${type}-divider`} sx={{ my: 0.5 }} /> : null,
            <MenuItem
              onClick={(e) => handleStatusChange(type)}
              key={type}
              selected={isCurrent}
              className={cx(classes.menuItem, danger && classes.menuItemDanger)}
            >
              <span className={cx(classes.statusDot, classes[`statusDot_${key}`])} />
              <span className={classes.statusLabel}>{type}</span>
              {isCurrent && <CheckIcon className={classes.statusCheck} />}
            </MenuItem>,
          ];
        })}
      </Menu>
    );
  }

  const makeRow = (row, isItemSelected, index, labelId) => {
    let keys = Object.keys(row);
    const isTimeFrame = title.toLowerCase() === "time frame";
    const isAdminList = isTimeFrame || title.toLowerCase() === "holidays";

    return (
      <TableRow
        hover
        role="checkbox"
        aria-checked={isItemSelected}
        tabIndex={-1}
        key={index}
        selected={isItemSelected}
        className={classes.tableRow}
        onClick={() => {
          if (handleRowClick) {
            handleRowClick(row)
          }
        }}
        style={{ height: dense ? 33 : 62.2, maxHeight: dense ? 33 : 62.2 }}
      >
        {tab !== "deleted" && selectable ? (
          <TableCell padding="checkbox">
            <Checkbox
              checked={isItemSelected}
              onClick={(event) => handleClick(event, row)}
              slotProps={{
                input: { "aria-labelledby": labelId }
              }}
            />
          </TableCell>
        ) : null}
        <TableCell component="th" id={labelId} scope="row">
          {type === "contact"
            ? <ContactNameCell name={row[keys[0]]} contact={row.object} showKind={tab === "all"} />
            : isAdminList
              ? (
                <ListParty
                  icon={isTimeFrame ? <TodayIcon /> : <EventIcon />}
                  tone={row.active ? "brand" : "neutral"}
                  title={row[keys[0]]}
                />
              )
              : row[keys[0]]}
        </TableCell>
        {keys.map((item) =>
          keys.indexOf(item) !== 0 &&
            item !== "actions" &&
            item !== "object" &&
            // The hours of a day share one cell (see renderTimeFrameTime).
            !(isTimeFrame && (item === "allDay" || item === "end")) ? (
            <TableCell align={cellAlign ? cellAlign : "right"} key={item}>
              {renderTableCell(item, row[item], row, title)}
            </TableCell>
          ) : null
        )}
        {keys.indexOf("actions") !== -1 ? (
          <TableCell align="right" className={classes.actions}>
            <ActionsSpeedDial
              actions={row["actions"]}
              rowItem={row}
              history={history}
              handleDialClick={handleDialClick}
              setCalling={setCalling}
              setValue={setValue}
              setOpenContactInfo={setOpenContactInfo}
              setEditContactInfo={setEditContactInfo}
              setContact={setContact}
              setEditTimeFrameDay={setEditTimeFrameDay}
              setTimeFrameDay={setTimeFrameDay}
              setEditHoliday={setEditHoliday}
              setHoliday={setHoliday}
              setForwardItem={setForwardItem}
              setForward={setForward}
              setDeleteForever={setDeleteForever}
              setDeleteTarget={setDeleteTarget}
              handleDelete={handleDelete}
              handleDeleteForever={handleDeleteForever}
              setPlay={setPlay}
              setPlayTarget={setPlayTarget}
              setPlayTargetObject={setPlayTargetObject}
              setTitleChangeItem={setTitleChangeItem}
              setEditTitle={setEditTitle}
              tab={tab}
              setAttachment={setAttachment}
              setRecipient={setRecipient}
              setSendEmail={setSendEmail}
              setError={setError}
            />
          </TableCell>
        ) : null}
      </TableRow>
    );

  }

  return (
    <div className={classes.root}>
      {statusDropdownMenu()}
      <DeleteForeverConfirmation
        open={deleteForever}
        setOpen={setDeleteForever}
        handleDeleteForever={handleDeleteForever}
        item={deleteTarget}
        setError={setError}
        type={type}
      />
      <AudioPlayer
        open={play}
        setOpen={setPlay}
        src={playTarget}
        from={playTargetObject}
      />
      <Paper className={classes.paper}>
        <EnhancedTableToolbar
          numSelected={selected.length}
          title={title}
          menu={menu ? menu : false}
          searchFunc={searchFunc}
          tab={tab}
          history={history}
          hasClear={hasClear}
          clearFunction={clearFunction}
          setPage={setPage}
          handleDelete={handleDelete}
          setDeleteTarget={setDeleteTarget}
          items={selected}
          setSelected={setSelected}
          createItemCallback={createItemCallback}
          createItemTooltip={createItemTooltip}
          useGroupFilter={useGroupFilter}
          createGroupOptions={createGroupOptions}
          setFilterGroup={setFilterGroup}
          filterGroup={filterGroup}
          refreshDataOnSameTab={refreshDataOnSameTab}
        />
        {/* A thin bar while the list is being (re)loaded. It keeps its
            height when idle so the rows do not jump. */}
        <div className={classes.progress}>
          {loading ? <LinearProgress className={classes.progressBar} /> : null}
        </div>
        <TableContainer
          className={classes.tableContainer}
        >
          <Table
            stickyHeader
            className={classes.table}
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
            aria-label={title + " table"}
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rowData.length}
              dense={dense}
              data={headerData}
              selectable={tab !== "deleted" ? selectable : false}
              tab={tab}
            />
            <TableBody className={classes.tableBody}>
              {stableSort(rowData, getComparator(order, orderBy))
                .slice(shownPage * rowsPerPage, shownPage * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isItemSelected = isSelected(row);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return makeRow(row, isItemSelected, index, labelId);
                })}
              {rowData.length === 0 ? (
                <TableRow style={{ height: dense ? 33 : 62.2 }}>
                  <TableCell colSpan={10} style={{ borderBottom: 0 }}>
                    {loading
                      // Nothing to show yet is not the same as nothing there.
                      ? <span className={classes.loadingText}>Loading…</span>
                      : emptyText
                      ? emptyText
                      : type && type.toLowerCase() === "conference"
                      ? "No conferences found."
                      : type === "contact"
                        ? "No contacts found."
                        : type === "holiday"
                        ? "No holidays yet."
                        : title.toLowerCase() === "call journal"
                        ? "No calls found."
                        : type === "voicemail"
                        ? "No voicemails found."
                        : type === "fax"
                        ? "No faxes found."
                        : "All caught up!"}
                  </TableCell>
                </TableRow>
              ) : null}
              {/* {emptyRows > 0 && (
                <TableRow style={{ height: (dense ? 33 : 62.2) * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )} */}
            </TableBody>
          </Table>
        </TableContainer>
        {!noPagination && (
          <div className={classes.footerBar}>
            {/* Something a list adds beside the paging, such as "Load more". */}
            {footerAction ? <div className={classes.footerAction}>{footerAction}</div> : null}
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={rowData.length}
              rowsPerPage={rowsPerPage}
              page={shownPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              className={classes.pagination}
            />
          </div>
        )}
      </Paper>
    </div>
  );
}

EnhancedTable.propTypes = {
  selectable: PropTypes.bool.isRequired,
  rowData: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      status: PropTypes.string,
      dateTime: PropTypes.string,
    })
  ),
  headerData: PropTypes.shape({
    id: PropTypes.string,
    numeric: PropTypes.bool,
    disablePadding: PropTypes.bool,
    label: PropTypes.string,
  }),
  menu: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  searchFunc: PropTypes.func,
  title: PropTypes.string.isRequired,
  tab: PropTypes.string.isRequired,
  hasClear: PropTypes.bool.isRequired,
  hasActions: PropTypes.bool.isRequired,
  clearFunction: PropTypes.func,
  setPage: PropTypes.func.isRequired,
  emptyText: PropTypes.string,
  footerAction: PropTypes.node,
  loading: PropTypes.bool,
  setRowsPerPage: PropTypes.func.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  noPagination: PropTypes.bool,
  handleDialClick: PropTypes.func,
  setCalling: PropTypes.func,
  setValue: PropTypes.func,
  setOpenContactInfo: PropTypes.func,
  setEditContactInfo: PropTypes.func,
  setContact: PropTypes.func,
  setEditTimeFrameDay: PropTypes.func,
  setTimeFrameDay: PropTypes.func,
  setEditHoliday: PropTypes.func,
  setHoliday: PropTypes.func,
  setForwardItem: PropTypes.func,
  setForward: PropTypes.func,
  statusChangeFunction: PropTypes.func,
  handleDelete: PropTypes.func,
  handleDeleteForever: PropTypes.func,
  createItemCallback: PropTypes.func,
  createItemTooltip: PropTypes.string,
  useGroupFilter: PropTypes.bool.isRequired,
  createGroupOptions: PropTypes.func,
  setFilterGroup: PropTypes.func,
  filterGroup: PropTypes.string,
  statusOptions: PropTypes.arrayOf(PropTypes.string),
  dense: PropTypes.bool,
  refreshDataOnSameTab: PropTypes.func,
  handleRowClick: PropTypes.func,
  cellAlign: PropTypes.string,
};